/**
 * MultiSeriesChart — 공용 SVG 다중 시리즈 차트 (line / area / bar / mixed / stacked).
 *
 * 대시보드 위젯에서 시작해 전 화면 공용으로 분리(2026-09-04). caller 가 series prep 을 책임지고,
 * 이 컴포넌트는 축(niceScale)·grid·듀얼축/정규화·hover 툴팁·범례 dim·도트 모드를 담당한다.
 *
 *   <MultiSeriesChart labels={["1월", …]} series={[…]} options={{ stylePreset: "bar", stacked: true }} height={180} />
 *
 * 색은 `var(--chart-*)` 토큰(components/charts/tokens.ts) 만 쓴다.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { ChartTooltip } from "@/components/ui/ChartTooltip";
import { useHoverDismiss } from "@/components/ui/useHoverDismiss";
import { Icon } from "@/components/ui/Icon";
import { niceScale } from "./niceScale";
import { useContainerSize } from "./useContainerSize";
import { useDotMode } from "./dot-mode-context";
import { CHART_TOKENS } from "./tokens";

export type ChartStylePreset =
  | "basic-line"
  | "smooth-line"
  | "area-line"
  | "bar"
  | "mixed"
  | "minimal";

export type ChartYAxisMode = "auto" | "single" | "dual" | "normalize";

export interface ChartSeries {
  /** unique key (axisAssignment/focus용) */
  code: string;
  name: string;
  color: string;
  /** 단위 표시 — 툴팁에 vals와 함께 (예: "천원/톤" 또는 "㎥") */
  unitForTooltip: string;
  /** normalize base 계산용 원본 */
  raw: (number | null)[];
  /** 표시값 (이미 천원/㎥ 등 단위변환된 것) */
  vals: (number | null)[];
  /** mixed 프리셋에서 이 시리즈를 line/bar 중 무엇으로 그릴지. mixedLineFn 보다 우선 */
  kind?: "line" | "bar";
  /** 기준 시리즈 강조 — 선을 조금 굵게 */
  emphasis?: boolean;
  /**
   * 추정/예정값 (bklit 프로젝션 라인 차용). 값이 있는 인덱스마다 직전 실적점에서 점선을 긋고
   * 끝에 빈 마커를 찍는다. 툴팁에는 "~값 (추정)" 행으로 따로 표시 — 실적과 섞이지 않는다.
   */
  projected?: (number | null)[];
}

/** 대시보드 WidgetSettings 는 이 타입의 상위집합이라 그대로 넘길 수 있다 */
export interface ChartOptions {
  /** 기본 "basic-line" */
  stylePreset?: ChartStylePreset;
  /** 기본 "auto" (= single + 자동 추천 배너) */
  yAxisMode?: ChartYAxisMode;
  /** dual 모드에서 series별 축 할당. 미설정→"left" */
  axisAssignment?: Record<string, "left" | "right">;
  /** 범례 hover 시 다른 series dim — 기본 true */
  focusEnabled?: boolean;
  /** 값 차이가 클 때 듀얼축/정규화 추천 배너 — 기본 true */
  autoSuggestEnabled?: boolean;
  /** bar 시리즈를 위로 쌓는다(stacked). 축 범위는 합계 기준 */
  stacked?: boolean;
  /** 좌측 Y축 눈금 숫자 표시. false 면 grid 선만 남기고 숫자를 뺀다 (작은 카드용). 기본 true */
  showYAxis?: boolean;
  /** 커서가 가리키는 X 라벨을 알약(pill)으로 강조 (bklit 차용). 기본 true */
  xHoverPill?: boolean;
  /** 커서 인덱스의 값을 Y축 위 알약으로 띄운다 (X 알약과 대칭, showYAxis 일 때만). 기본 true */
  yHoverPill?: boolean;
}

interface Props {
  /** X 축 라벨 (카테고리) */
  labels: string[];
  series: ChartSeries[];
  options?: ChartOptions;
  emptyText?: string;
  /** mixed preset에서 line으로 그릴 series 판별 (없으면 마지막 series 기본). series.kind 가 있으면 그것이 우선 */
  mixedLineFn?: (s: ChartSeries, i: number, all: ChartSeries[]) => boolean;
  onOpenSettings?: () => void;
  /** wrapper가 owner — 카드 헤더 legend hover와 동기 */
  focusedCode?: string | null;
  /** 강조할 X 인덱스 (예: 당월). 나머지 막대는 살짝 흐리게 */
  highlightIndex?: number | null;
  /** 호스트 높이(px). 생략하면 부모 flex 에 맡긴다 (.widget-chart 패턴) */
  height?: number;
  /** 툴팁 헤더 (기본 = labels[i]) */
  tooltipHead?: (i: number) => ReactNode;
  /** 툴팁에 합계 행 추가 (기본 = stacked 일 때만) */
  tooltipTotal?: boolean;
  /** 앞 X값 대비 증감. 시간축이 아닌 카테고리 비교에서는 false */
  tooltipComparePrevious?: boolean;
  /** 값 표기 (툴팁·합계). 기본 toLocaleString */
  valueFormatter?: (v: number) => string;
  /** 로딩 중 — 축은 두고 플롯 영역에 펄스 라인을 그린다 (스켈레톤 패턴). 툴팁 비활성 */
  loading?: boolean;
  /**
   * 커서가 가리키는 X 인덱스를 밖으로 알린다 (떠나면 null). bklit 식 "커서 따라 바뀌는 숫자" 판을
   * 차트 밖에 두고 싶을 때 ChartReadout 과 함께 쓴다.
   */
  onHoverIndex?: (i: number | null) => void;
  /** 떠 있는 툴팁 표시 여부. 밖에 readout 을 둘 때 false 로 끄고 크로스헤어만 남긴다. 기본 true */
  showTooltip?: boolean;
  /** 스크린리더용 설명 */
  ariaLabel?: string;
  className?: string;
}

const defaultFormat = (v: number) => v.toLocaleString();

export function MultiSeriesChart({
  labels,
  series,
  options = {},
  emptyText = "데이터 없음",
  mixedLineFn,
  onOpenSettings,
  focusedCode = null,
  highlightIndex = null,
  height,
  tooltipHead,
  tooltipTotal,
  tooltipComparePrevious = true,
  valueFormatter = defaultFormat,
  loading = false,
  onHoverIndex,
  showTooltip = true,
  ariaLabel,
  className,
}: Props) {
  const dotMode = useDotMode();
  const isDot = dotMode.enabled;
  const stylePreset = options.stylePreset ?? "basic-line";
  // "auto"는 렌더링상 single과 동일 동작 + autoSuggest 배너 활성 (사용자가 명시 설정 안 함)
  const rawMode: ChartYAxisMode = options.yAxisMode ?? "auto";
  const yAxisMode: ChartYAxisMode = rawMode === "auto" ? "single" : rawMode;
  const axisAssignment = options.axisAssignment ?? {};
  const focusEnabled = options.focusEnabled ?? true;
  const autoSuggestEnabled = options.autoSuggestEnabled ?? true;
  const stacked = options.stacked ?? false;
  const showYAxis = options.showYAxis ?? true;
  const xHoverPill = options.xHoverPill ?? true;
  const yHoverPill = options.yHoverPill ?? true;
  const showTotal = tooltipTotal ?? stacked;

  const isMinimal = stylePreset === "minimal";
  const isSmooth = stylePreset === "smooth-line";
  const isArea = stylePreset === "area-line";
  const isBar = stylePreset === "bar";
  const isMixed = stylePreset === "mixed";

  // mixed: 어느 series를 line으로 그릴지 — series.kind > mixedLineFn > 마지막 series
  const defaultMixedLine = (_s: ChartSeries, i: number, all: ChartSeries[]) => i === all.length - 1;
  const lineFn = mixedLineFn ?? defaultMixedLine;
  const isSeriesLine = (s: ChartSeries, i: number) => {
    if (s.kind) return s.kind === "line";
    if (isMixed) return lineFn(s, i, series);
    return !isBar; // bar 프리셋이면 모두 bar, 그 외엔 모두 line류
  };

  // normalize 변환은 내부에서 처리 — 첫 유효값 = 100
  const seriesDisplay = useMemo(() => {
    return series.map((s, i) => {
      let vals = s.vals;
      if (yAxisMode === "normalize") {
        const baseIdx = s.raw.findIndex((v) => v != null && v !== 0);
        const base = baseIdx >= 0 ? s.raw[baseIdx] : null;
        vals = s.raw.map((v) => (v == null || base == null ? null : Math.round((v / base) * 1000) / 10));
      }
      return {
        ...s,
        vals,
        axis: (axisAssignment[s.code] ?? "left") as "left" | "right",
        isLine: isSeriesLine(s, i),
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [series, yAxisMode, axisAssignment, stylePreset, mixedLineFn]);

  const axisRange = useMemo(() => {
    const collect = (which: "left" | "right") => {
      const ss = yAxisMode === "dual"
        ? seriesDisplay.filter((s) => s.axis === which)
        : (which === "left" ? seriesDisplay : []);
      const all: number[] = [];
      if (stacked) {
        // 막대는 인덱스별 합계가 축 범위를 결정, 선은 개별값
        const bars = ss.filter((s) => !s.isLine);
        const n = Math.max(0, ...bars.map((s) => s.vals.length));
        for (let i = 0; i < n; i++) {
          let sum = 0;
          let any = false;
          for (const s of bars) {
            const v = s.vals[i];
            if (v != null) { sum += v; any = true; }
          }
          if (any) all.push(sum);
        }
        for (const s of ss) if (s.isLine) all.push(...s.vals.filter((v): v is number => v != null));
      } else {
        for (const s of ss) all.push(...s.vals.filter((v): v is number => v != null));
      }
      for (const s of ss) if (s.projected) all.push(...s.projected.filter((v): v is number => v != null));
      if (all.length === 0) return { min: 0, max: 100, range: 100, hasData: false, ticks: [] as number[] };
      const rawMin = Math.min(...all);
      const rawMax = Math.max(...all);
      const pad = (rawMax - rawMin || Math.abs(rawMax) || 1) * 0.05;
      // 양수만 있는 데이터는 min을 0 이하로 내리지 않음 (불필요한 음수 tick 방지)
      // 막대(stacked 포함)는 바닥이 0 이어야 높이가 의미를 가진다
      const hasBar = ss.some((s) => !s.isLine);
      const minInput = hasBar && rawMin >= 0 ? 0 : rawMin >= 0 ? Math.max(0, rawMin - pad) : rawMin - pad;
      const scale = niceScale(minInput, hasBar ? Math.max(0, rawMax + pad) : rawMax + pad);
      return { min: scale.min, max: scale.max, range: scale.max - scale.min || 1, hasData: true, ticks: scale.ticks };
    };
    return { left: collect("left"), right: collect("right") };
  }, [seriesDisplay, yAxisMode, stacked]);

  const suggest = useMemo(() => {
    if (!autoSuggestEnabled || yAxisMode !== "single" || seriesDisplay.length < 2) return null;
    const avgs = seriesDisplay
      .map((s) => {
        const nums = s.vals.filter((v): v is number => v != null && v > 0);
        if (nums.length === 0) return null;
        return nums.reduce((a, b) => a + b, 0) / nums.length;
      })
      .filter((a): a is number => a != null);
    if (avgs.length < 2) return null;
    const ratio = Math.max(...avgs) / Math.min(...avgs);
    return ratio >= 5 ? Math.round(ratio * 10) / 10 : null;
  }, [seriesDisplay, autoSuggestEnabled, yAxisMode]);

  const isDimmed = (code: string) =>
    focusEnabled && focusedCode !== null && focusedCode !== code;

  const [hostRef, { w, h }] = useContainerSize(height ? { w: 540, h: height } : undefined);
  /** 대략적 글자 폭 — 한글은 폰트 크기, 영숫자는 0.62배 (SVG 라 measureText 없이 추정) */
  const textW = (label: string) =>
    [...label].reduce((acc, ch) => acc + (ch.charCodeAt(0) > 0x7f ? CHART_TOKENS.axisFontSize : CHART_TOKENS.axisFontSize * 0.62), 0);
  // 좌측 여백 — 기본 48, 눈금 숫자가 길면(6자리 이상) 잘리지 않을 만큼 넓힌다
  const leftTickW = showYAxis ? Math.max(0, ...axisRange.left.ticks.map((t) => textW(t.toLocaleString()))) : 0;
  const pad = isMinimal
    ? { l: 4, r: 4, t: 6, b: 4 }
    : {
        l: showYAxis ? Math.max(48, Math.ceil(leftTickW) + 22) : 14,
        r: yAxisMode === "dual" && axisRange.right.hasData ? 48 : 16,
        t: 26,
        b: 34,
      };
  const n = labels.length;
  const plotW = w - pad.l - pad.r;
  const hasBars = seriesDisplay.some((s) => !s.isLine);
  // 좌우 안쪽 여백 — 막대는 슬롯 절반(첫/끝 막대가 가장자리에 붙어 폭을 다 쓴다), 선은 폭의 8%(최대 32)
  const X_INSET = n > 1 ? (hasBars ? plotW / (2 * n) : Math.min(32, plotW * 0.08)) : 0;
  /** 막대 슬롯 폭 — inset 이 슬롯 절반이므로 plotW/n 이 곧 막대 간격 */
  const slotStep = plotW / Math.max(1, n);
  const xFor = (i: number) => {
    if (n === 1) return pad.l + plotW / 2;
    return pad.l + X_INSET + (i / (n - 1)) * (plotW - 2 * X_INSET);
  };
  const yForAxis = (v: number, axis: "left" | "right") => {
    const r = yAxisMode === "dual" ? axisRange[axis] : axisRange.left;
    return pad.t + (1 - (v - r.min) / r.range) * (h - pad.t - pad.b);
  };
  const yRatio = (axis: "left" | "right", v: number) => {
    const r = yAxisMode === "dual" ? axisRange[axis] : axisRange.left;
    return (v - r.min) / r.range;
  };
  const [hover, setHover] = useState<{ i: number; clientX: number; clientY: number } | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  // 터치: 스크롤·바깥 탭 시 hover 정리 (mouseleave 가 안 오는 환경)
  useHoverDismiss(hover != null, () => setHover(null), svgRef);
  const hoverIdx = hover?.i ?? null;
  useEffect(() => {
    onHoverIndex?.(loading ? null : hoverIdx);
  }, [hoverIdx, loading, onHoverIndex]);

  const onMove = (e: React.MouseEvent<SVGSVGElement>) => {
    if (!svgRef.current) return;
    const rect = svgRef.current.getBoundingClientRect();
    const px = ((e.clientX - rect.left) / rect.width) * w;
    let i = n > 1
      ? Math.round(((px - pad.l - X_INSET) / (plotW - 2 * X_INSET)) * (n - 1))
      : 0;
    i = Math.max(0, Math.min(n - 1, i));
    setHover({ i, clientX: e.clientX, clientY: e.clientY });
  };

  const buildPath = (vals: (number | null)[], axis: "left" | "right"): string => {
    const pts: { x: number; y: number }[] = [];
    vals.forEach((v, i) => {
      if (v == null) return;
      pts.push({ x: xFor(i), y: yForAxis(v, axis) });
    });
    if (pts.length === 0) return "";
    if (!isSmooth || pts.length < 2) {
      return pts.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(" ");
    }
    const out: string[] = [`M ${pts[0].x.toFixed(1)} ${pts[0].y.toFixed(1)}`];
    for (let i = 0; i < pts.length - 1; i++) {
      const p0 = pts[i - 1] ?? pts[i];
      const p1 = pts[i];
      const p2 = pts[i + 1];
      const p3 = pts[i + 2] ?? pts[i + 1];
      const t = 0.3;
      const cp1x = p1.x + (p2.x - p0.x) * t;
      const cp1y = p1.y + (p2.y - p0.y) * t;
      const cp2x = p2.x - (p3.x - p1.x) * t;
      const cp2y = p2.y - (p3.y - p1.y) * t;
      out.push(`C ${cp1x.toFixed(1)} ${cp1y.toFixed(1)} ${cp2x.toFixed(1)} ${cp2y.toFixed(1)} ${p2.x.toFixed(1)} ${p2.y.toFixed(1)}`);
    }
    return out.join(" ");
  };

  // bar 그릴 series — mixed에선 line 제외, 순수 bar 프리셋에선 모두
  const barSeries = seriesDisplay.map((s, i) => ({ s, i })).filter(({ s }) => !s.isLine);

  /** 막대 불투명도 — 범례 dim > hover 외 인덱스 > highlight 외 인덱스 */
  const barOpacity = (code: string, i: number) => {
    if (isDimmed(code)) return 0.28;
    if (hover != null) return hover.i === i ? 0.95 : 0.28;
    if (highlightIndex != null) return highlightIndex === i ? 0.95 : 0.72;
    return 0.92;
  };

  const renderBars = () => {
    if (n === 0 || barSeries.length === 0) return null;
    const groupW = slotStep;
    if (stacked) {
      const barW = Math.max(2, groupW * 0.62);
      const out: ReactNode[] = [];
      for (let i = 0; i < n; i++) {
        let cum = 0;
        // 맨 위 조각만 모서리를 둥글게
        let topIdx = -1;
        barSeries.forEach(({ s, i: si }) => { const v = s.vals[i]; if (v != null && v > 0) topIdx = si; });
        barSeries.forEach(({ s, i: si }) => {
          const v = s.vals[i];
          if (v == null || v <= 0) return;
          const y0 = yForAxis(cum, s.axis);
          const y1 = yForAxis(cum + v, s.axis);
          cum += v;
          out.push(
            <rect
              key={`${si}-${i}`}
              x={xFor(i) - barW / 2}
              y={y1}
              width={barW}
              height={Math.max(0, y0 - y1)}
              fill={s.color}
              opacity={barOpacity(s.code, i)}
              rx={si === topIdx ? CHART_TOKENS.barRadius : 0}
            />,
          );
        });
      }
      return out;
    }
    const barW = Math.max(2, (groupW * 0.7) / Math.max(1, barSeries.length));
    return barSeries.map(({ s, i: si }, orderInBars) =>
      s.vals.map((v, i) => {
        if (v == null) return null;
        const x = xFor(i) - (barSeries.length * barW) / 2 + orderInBars * barW;
        const y = yForAxis(v, s.axis);
        const zeroY = yForAxis(0, s.axis);
        return (
          <rect
            key={`${si}-${i}`}
            x={x}
            y={Math.min(y, zeroY)}
            width={barW * 0.9}
            height={Math.abs(zeroY - y)}
            fill={s.color}
            opacity={barOpacity(s.code, i)}
            rx={CHART_TOKENS.barRadius}
          />
        );
      }),
    );
  };

  // 도트(픽셀아트) 모드 — 모든 series를 정사각형 픽셀을 바닥부터 쌓은 스택형 바로 그린다.
  // stylePreset(라인/바/혼합) 무시. crispEdges로 안티앨리어싱 제거 → 레트로 픽셀 느낌.
  const renderDotBars = () => {
    if (n === 0 || seriesDisplay.length === 0) return null;
    const groupW = slotStep;
    const cnt = Math.max(1, seriesDisplay.length);
    const slotW = (groupW * 0.78) / cnt;
    const CELL = Math.max(5, Math.min(12, Math.round(slotW * 0.92)));
    const gap = Math.max(1, Math.round(CELL * 0.18));
    const sq = CELL - gap;
    const baseY = h - pad.b;
    return seriesDisplay.map((s, si) =>
      s.vals.map((v, i) => {
        if (v == null) return null;
        const xCenter = xFor(i) - (cnt * slotW) / 2 + si * slotW + slotW / 2;
        const x = xCenter - sq / 2;
        const yTop = yForAxis(v, s.axis);
        const fullH = Math.max(0, baseY - yTop);
        const cells = Math.max(v > 0 ? 1 : 0, Math.round(fullH / CELL));
        if (cells === 0) return null;
        const dim = isDimmed(s.code) || (hover != null && hover.i !== i);
        const squares = [];
        for (let k = 0; k < cells; k++) {
          squares.push(
            <rect
              key={k}
              x={x}
              y={baseY - (k + 1) * CELL + gap / 2}
              width={sq}
              height={sq}
              fill={s.color}
              opacity={dim ? 0.28 : 0.95}
              shapeRendering="crispEdges"
            />,
          );
        }
        return <g key={`dot-${si}-${i}`}>{squares}</g>;
      }),
    );
  };

  // X 라벨 간격 — 좁으면 건너뛰기 (마지막 라벨은 유지)
  const labelStep = n > 1
    ? Math.max(1, Math.ceil((CHART_TOKENS.minXLabelPx * n) / Math.max(1, plotW - 2 * X_INSET + CHART_TOKENS.minXLabelPx)))
    : 1;
  // 마지막 라벨을 기준으로 간격을 맞춘다 (12월이 항상 보이고, 11월·12월 겹침 없음)
  const showLabel = (i: number) => (n - 1 - i) % labelStep === 0;

  const hostStyle = height ? { height } : undefined;
  const pillIdx = xHoverPill && !isMinimal && !loading && hover ? hover.i : null;
  // 알약 — 폭은 라벨 중 최대치로 고정(이동 중 폭이 출렁이지 않게), 위치는 CSS transform 트랜지션으로 미끄러진다
  const pillW = Math.ceil(Math.max(0, ...labels.map(textW)) + 18);
  const pillH = 20;
  const pill = pillIdx != null ? (() => {
    const cx = Math.max(pad.l - 8 + pillW / 2, Math.min(w - pad.r + 8 - pillW / 2, xFor(pillIdx)));
    return { idx: pillIdx, label: labels[pillIdx], cx };
  })() : null;
  /** bklit 방식: 알약 반폭 안은 숨김, 그 밖 20px 구간은 선형 페이드 */
  const labelOpacity = (i: number) => {
    if (!pill) return 1;
    const d = Math.abs(xFor(i) - pill.cx);
    const half = pillW / 2;
    if (d < half) return 0;
    if (d < half + 20) return (d - half) / 20;
    return 1;
  };

  /**
   * Y축 알약 — 커서 인덱스의 값을 축 숫자 위에 띄운다 (X 알약과 대칭).
   * stacked 막대는 합계 하나(중립색), 선·비스택 시리즈는 각자 색으로. 같은 쪽끼리 겹치면 위아래로 밀어낸다.
   * yLine = 값의 실제 높이(가이드선), y = 밀어낸 뒤 알약 위치.
   */
  type YPill = { key: string; y: number; yLine: number; label: string; color: string | null; side: "left" | "right" };
  const yPillH = 18;
  const yPills: YPill[] = (() => {
    if (!yHoverPill || !showYAxis || isMinimal || loading || !hover) return [];
    const i = hover.i;
    const out: YPill[] = [];
    const sides: ("left" | "right")[] = yAxisMode === "dual" && axisRange.right.hasData ? ["left", "right"] : ["left"];
    for (const side of sides) {
      const ss = seriesDisplay.filter((s) => (yAxisMode === "dual" ? s.axis === side : true) && !isDimmed(s.code));
      const push = (key: string, v: number, color: string | null) =>
        out.push({ key: `${side}-${key}`, y: yForAxis(v, side), yLine: yForAxis(v, side), label: valueFormatter(v), color, side });
      if (stacked) {
        let sum = 0;
        let any = false;
        for (const s of ss) {
          if (s.isLine) continue;
          const v = s.vals[i];
          if (v != null) { sum += v; any = true; }
        }
        if (any) push("total", sum, null);
        for (const s of ss) if (s.isLine && s.vals[i] != null) push(s.code, s.vals[i] as number, s.color);
      } else {
        for (const s of ss) if (s.vals[i] != null) push(s.code, s.vals[i] as number, s.color);
      }
      // 겹침 해소 — 위에서 아래로 정렬해 알약 높이만큼 내려 밀고, 바닥을 넘으면 다시 위로
      const mine = out.filter((p) => p.side === side).sort((a, b) => a.y - b.y);
      const gap = yPillH + 2;
      for (let k = 1; k < mine.length; k++) mine[k].y = Math.max(mine[k].y, mine[k - 1].y + gap);
      const maxY = h - yPillH / 2;
      for (let k = mine.length - 1; k >= 0; k--) {
        const limit = k === mine.length - 1 ? maxY : mine[k + 1].y - gap;
        mine[k].y = Math.min(mine[k].y, limit);
      }
      for (const p of mine) p.y = Math.max(yPillH / 2, p.y);
    }
    return out;
  })();
  /** Y 눈금 숫자 — 알약이 덮는 구간은 숨기고 그 밖 12px 은 선형 페이드 (X 라벨과 같은 규칙) */
  const tickOpacity = (side: "left" | "right", y: number) => {
    let o = 1;
    for (const p of yPills) {
      if (p.side !== side) continue;
      const d = Math.abs(y - p.y);
      const half = yPillH / 2;
      const v = d < half ? 0 : d < half + 12 ? (d - half) / 12 : 1;
      o = Math.min(o, v);
    }
    return o;
  };

  return (
    <div className={`chart-host${loading ? " is-loading" : ""}${className ? ` ${className}` : ""}`} ref={hostRef} style={hostStyle}>
      {suggest != null && (
        <div className="lc-suggest" role="status">
          <Icon name="help" size={12} />
          <span>
            값 차이가 약 <strong>{suggest}배</strong> 입니다. <em>듀얼 축</em> 또는 <em>정규화</em> 모드를 추천합니다.
          </span>
          {onOpenSettings && (
            <button type="button" className="lc-suggest-btn" onClick={onOpenSettings}>
              설정 열기
            </button>
          )}
        </div>
      )}
      <svg
        ref={svgRef}
        viewBox={`0 0 ${w} ${h}`}
        className={`line-chart ${isDot ? "line-chart--dot" : ""}`}
        width="100%"
        height="100%"
        role="img"
        aria-label={ariaLabel}
        onMouseMove={onMove}
        onMouseLeave={() => setHover(null)}
      >
        {/* Y축 좌측 grid + tick label — niceScale ticks 사용. i===0 (bottom baseline) line 생략 */}
        {!isMinimal && axisRange.left.ticks.map((tick, i) => {
          const y = pad.t + (1 - yRatio("left", tick)) * (h - pad.t - pad.b);
          const isBaseline = i === 0;
          return (
            <g key={`gl-${tick}-${i}`}>
              {!isBaseline && (
                <line
                  x1={pad.l}
                  x2={w - pad.r}
                  y1={y}
                  y2={y}
                  stroke={CHART_TOKENS.gridColor}
                  strokeOpacity={CHART_TOKENS.gridOpacity}
                />
              )}
              {showYAxis && (
                <text
                  x={pad.l - 8}
                  y={y + 3}
                  textAnchor="end"
                  fontSize={CHART_TOKENS.axisFontSize}
                  fill={CHART_TOKENS.axisLabelColor}
                  fontWeight="500"
                  className="chart-xlabel"
                  style={{ opacity: tickOpacity("left", y) }}
                >
                  {tick.toLocaleString()}
                </text>
              )}
            </g>
          );
        })}
        {/* Y축 우측 tick label (dual 전용) — 색·폰트 모두 좌측과 동일 (neutral gray) */}
        {!isMinimal && yAxisMode === "dual" && axisRange.right.hasData && axisRange.right.ticks.map((tick, i) => {
          const y = pad.t + (1 - yRatio("right", tick)) * (h - pad.t - pad.b);
          return (
            <text
              key={`gr-${tick}-${i}`}
              x={w - pad.r + 6}
              y={y + 3}
              textAnchor="start"
              fontSize={CHART_TOKENS.axisFontSize}
              fill={CHART_TOKENS.axisLabelColor}
              fontWeight="500"
              className="chart-xlabel"
              style={{ opacity: tickOpacity("right", y) }}
            >
              {tick.toLocaleString()}
            </text>
          );
        })}
        {/* 도트 모드 — 스택형 픽셀 바 (라인/바 분기 모두 대체) */}
        {isDot && renderDotBars()}
        {/* bar 시리즈 먼저 (line 아래) */}
        {!isDot && renderBars()}
        {/* line/area 계열 시리즈 */}
        {!isDot && seriesDisplay.map((s, si) => {
          if (!s.isLine) return null;
          const path = buildPath(s.vals, s.axis);
          let areaPath = "";
          if (isArea && path) {
            const firstNonNull = s.vals.findIndex((v) => v != null);
            const lastNonNull = s.vals.length - 1 - [...s.vals].reverse().findIndex((v) => v != null);
            if (firstNonNull >= 0 && lastNonNull >= 0) {
              const baseY = h - pad.b;
              areaPath = `${path} L ${xFor(lastNonNull).toFixed(1)} ${baseY} L ${xFor(firstNonNull).toFixed(1)} ${baseY} Z`;
            }
          }
          const dim = isDimmed(s.code);
          const op = dim ? 0.25 : 1;
          return (
            <g key={si} opacity={op}>
              {areaPath && <path d={areaPath} fill={s.color} fillOpacity="0.14" stroke="none" />}
              <path
                d={path}
                fill="none"
                stroke={s.color}
                strokeWidth={s.emphasis ? CHART_TOKENS.lineStrokeWidthEmphasis : CHART_TOKENS.lineStrokeWidth}
                strokeLinejoin="round"
                strokeLinecap="round"
              />
              {!isMinimal && s.vals.map((v, i) =>
                v == null ? null : (
                  <circle
                    key={i}
                    cx={xFor(i)}
                    cy={yForAxis(v, s.axis)}
                    r={hover?.i === i ? CHART_TOKENS.pointRadiusHover : CHART_TOKENS.pointRadius}
                    fill="var(--c-surface)"
                    stroke={s.color}
                    strokeWidth={CHART_TOKENS.lineBorderWidth}
                  />
                ),
              )}
            </g>
          );
        })}
        {/* 추정/예정 프로젝션 — 직전 실적점 → 추정점 점선 + 빈 마커 */}
        {!isDot && seriesDisplay.map((s, si) => {
          if (!s.projected) return null;
          const dim = isDimmed(s.code);
          return (
            <g key={`pj-${si}`} opacity={dim ? 0.25 : 0.85}>
              {s.projected.map((pv, i) => {
                if (pv == null) return null;
                let prev = i - 1;
                while (prev >= 0 && s.vals[prev] == null) prev--;
                if (prev < 0) return null;
                const x0 = xFor(prev), y0 = yForAxis(s.vals[prev] as number, s.axis);
                const x1 = xFor(i), y1 = yForAxis(pv, s.axis);
                return (
                  <g key={i}>
                    <line x1={x0} y1={y0} x2={x1} y2={y1} stroke={s.color} strokeWidth={CHART_TOKENS.lineStrokeWidth} strokeDasharray="4 3" strokeLinecap="round" />
                    <circle cx={x1} cy={y1} r={CHART_TOKENS.pointRadius + 0.5} fill="var(--c-surface)" stroke={s.color} strokeWidth={CHART_TOKENS.lineBorderWidth} strokeDasharray="2 1.5" />
                  </g>
                );
              })}
            </g>
          );
        })}
        {hover && !isMinimal && !loading && (
          <g className="chart-xanim" style={{ transform: `translate(${xFor(hover.i)}px, 0)` }}>
            <line
              x1={0}
              x2={0}
              y1={pad.t}
              y2={h - pad.b}
              stroke={CHART_TOKENS.gridColor}
              strokeOpacity={CHART_TOKENS.hoverCrosshairOpacity}
            />
          </g>
        )}
        {/* X축 라벨 — 하단 padding 늘려 여유 확보. hover 인덱스는 아래 알약이 대신 그린다 */}
        {!isMinimal && labels.map((m, i) => showLabel(i) && (
          <text
            key={i}
            x={xFor(i)}
            y={h - 12}
            textAnchor="middle"
            fontSize={CHART_TOKENS.axisFontSize}
            fill={CHART_TOKENS.axisLabelColor}
            fontWeight="500"
            className="chart-xlabel"
            style={{ opacity: labelOpacity(i) }}
          >{m}</text>
        ))}
        {/* 커서 위치 X 라벨 알약 — 숨겨진(건너뛴) 라벨도 여기서 드러난다 */}
        {pill && (
          <g className="chart-xpill chart-xanim" style={{ transform: `translate(${pill.cx}px, 0)` }}>
            <rect x={-pillW / 2} y={h - 12 - 14} width={pillW} height={pillH} rx={pillH / 2} />
            <text key={pill.label} x={0} y={h - 12} textAnchor="middle" fontSize={CHART_TOKENS.axisFontSize} fontWeight="700">{pill.label}</text>
          </g>
        )}
        {/* Y축 알약 + 가로 가이드선 — 커서 막대(점)의 높이에서 축까지 잇는다 */}
        {hover && yPills.map((p) => {
          const x = xFor(hover.i);
          const pw = Math.ceil(textW(p.label)) + 14;
          const edge = p.side === "left" ? pad.l - 5 : w - pad.r + 5;
          const rectX = p.side === "left" ? edge - pw : edge;
          const textX = p.side === "left" ? edge - 7 : edge + 7;
          return (
            <g key={p.key}>
              <g className="chart-xanim" style={{ transform: `translate(0, ${p.yLine}px)` }}>
                <line
                  x1={p.side === "left" ? pad.l : x}
                  x2={p.side === "left" ? x : w - pad.r}
                  y1={0}
                  y2={0}
                  stroke={p.color ?? CHART_TOKENS.gridColor}
                  strokeOpacity={p.color ? 0.55 : CHART_TOKENS.hoverCrosshairOpacity}
                  strokeDasharray="3 3"
                />
              </g>
              <g className="chart-ypill chart-xanim" style={{ transform: `translate(0, ${p.y}px)` }}>
                <rect x={rectX} y={-yPillH / 2} width={pw} height={yPillH} rx={yPillH / 2} style={p.color ? { fill: p.color } : undefined} />
                <text key={p.label} x={textX} y={3.5} textAnchor={p.side === "left" ? "end" : "start"} fontSize={CHART_TOKENS.axisFontSize} fontWeight="700">{p.label}</text>
              </g>
            </g>
          );
        })}
        {seriesDisplay.length === 0 && (
          <text x={w / 2} y={h / 2} textAnchor="middle" fontSize="12" fill="var(--c-text-muted)">{emptyText}</text>
        )}
      </svg>
      {loading && (
        <div className="chart-loading" aria-hidden="true" style={{ left: pad.l, right: pad.r, top: pad.t, bottom: pad.b }}>
          <span className="chart-loading-line" />
        </div>
      )}
      {hover && !loading && showTooltip && seriesDisplay.length > 0 && (
        <ChartTooltip x={hover.clientX} y={hover.clientY}>
          <div className="dc-tip">
            <div className="dc-tip-head">{tooltipHead ? tooltipHead(hover.i) : labels[hover.i]}</div>
            <div className="dc-tip-rows">
              {seriesDisplay.map((s, si) => {
                const v = s.vals[hover.i];
                const prev = hover.i > 0 ? s.vals[hover.i - 1] : null;
                const delta =
                  v != null && prev != null
                    ? {
                        abs: v - prev,
                        pct: prev !== 0 ? ((v - prev) / Math.abs(prev)) * 100 : null,
                      }
                    : null;
                const unitTxt = yAxisMode === "normalize" ? "%" : s.unitForTooltip;
                const pj = s.projected?.[hover.i];
                return (
                  <div key={si} className="dc-tip-row">
                    <span className="dc-tip-dot" style={{ background: s.color }} />
                    <span className="dc-tip-label">{s.name}</span>
                    <span className="dc-tip-val">
                      {v == null ? "—" : valueFormatter(v)}
                      {v != null && <span className="dc-tip-unit">{unitTxt}</span>}
                    </span>
                    {tooltipComparePrevious && <span className={`dc-tip-delta ${!delta ? "is-na" : delta.abs >= 0 ? "is-up" : "is-down"}`}>
                      {delta && (delta.pct != null || delta.abs !== 0)
                        ? (
                            <>
                              {delta.abs >= 0 ? "▲" : "▼"}
                              {delta.pct != null
                                ? ` ${Math.abs(delta.pct).toFixed(1)}%`
                                : ` ${valueFormatter(Math.abs(delta.abs))}`}
                            </>
                          )
                        : "—"}
                    </span>}
                    {pj != null && (
                      <>
                        <span className="dc-tip-dot is-projected" style={{ borderColor: s.color }} />
                        <span className="dc-tip-label">{s.name} 추정</span>
                        <span className="dc-tip-val is-projected">~{valueFormatter(pj)}<span className="dc-tip-unit">{unitTxt}</span></span>
                        <span className="dc-tip-delta is-na">월말</span>
                      </>
                    )}
                  </div>
                );
              })}
              {showTotal && yAxisMode !== "normalize" && (() => {
                const total = seriesDisplay.reduce((acc, s) => acc + (s.vals[hover.i] ?? 0), 0);
                const unit = seriesDisplay[0]?.unitForTooltip ?? "";
                return (
                  <div className="dc-tip-row is-total">
                    <span className="dc-tip-dot" style={{ background: "transparent" }} />
                    <span className="dc-tip-label">합계</span>
                    <span className="dc-tip-val">
                      {valueFormatter(total)}
                      <span className="dc-tip-unit">{unit}</span>
                    </span>
                    <span className="dc-tip-delta is-na" />
                  </div>
                );
              })()}
            </div>
          </div>
        </ChartTooltip>
      )}
    </div>
  );
}
